import type { Element } from '../engine/element';
import { Battery, Capacitor, Fuse, Lamp, Led, Switch, Voltmeter, Ammeter, VoltageSource } from '../engine/elements';
import type { Bjt, Mosfet } from '../engine/semis';
import type { BmsModule, ChargerModule, DcDcModule } from '../engine/modules';
import type { ConstantPowerLoad } from '../engine/loads';
import { LOAD_PRESETS } from '../engine/loads';
import type { Port, SubcktInstance } from '../engine/subckt';
import type { LogicElement, LogicGate, LogicInput, LogicOutput, DFlipFlop } from '../engine/logic';
import type { Microcontroller, PushButton } from '../engine/mcu';
import { fmt } from '../engine/format';

export const GRID = 20; // 격자 한 칸 = 월드 좌표 20px

export interface DrawCtx {
  ctx: CanvasRenderingContext2D;
  /** 소자 몸통 기본 색 */
  color: string;
  selected: boolean;
  highlighted: boolean;
  /** 핀0 / 핀1 쪽 리드선 색 (전압 색상 표시용) */
  leadA?: string;
  leadB?: string;
  /** 소자가 놓인 각도 — 글자를 똑바로 세우는 데 쓴다 */
  angle?: number;
  /** 3핀 이상 소자: 핀별 리드선 색 */
  pinColors?: string[];
  /** 좌우 반전(-1) 여부 */
  flip?: number;
  showValues: boolean;
  running: boolean;
}

interface Geom {
  x1: number; y1: number; x2: number; y2: number;
  len: number; ux: number; uy: number;
  cx: number; cy: number; angle: number;
}

export function geometry(el: Element): Geom {
  const x1 = el.x1 * GRID;
  const y1 = el.y1 * GRID;
  const x2 = el.x2 * GRID;
  const y2 = el.y2 * GRID;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  return {
    x1, y1, x2, y2, len,
    ux: dx / len, uy: dy / len,
    cx: (x1 + x2) / 2, cy: (y1 + y2) / 2,
    angle: Math.atan2(dy, dx),
  };
}

/** 소자 아래에 표시할 값 라벨 */
export function valueLabel(el: Element): string {
  switch (el.type) {
    case 'resistor': return fmt(el.num('resistance', 0), 'Ω');
    case 'lamp': return fmt(el.num('resistance', 0), 'Ω');
    case 'battery': return fmt(el.num('emf', 0), 'V');
    case 'vsource': {
      const w = el.str('waveform', 'dc');
      return w === 'dc' ? fmt(el.num('voltage', 0), 'V') : `${fmt(el.num('voltage', 0), 'V')} ${fmt(el.num('frequency', 0), 'Hz')}`;
    }
    case 'csource': return fmt(el.num('amps', 0), 'A');
    case 'capacitor': return fmt(el.num('capacitance', 0), 'F');
    case 'inductor': return fmt(el.num('inductance', 0), 'H');
    case 'fuse': return fmt(el.num('rating', 0), 'A');
    case 'led': return fmt(el.num('vf', 0), 'V');
    case 'usb': return fmt(el.num('emf', 0), 'V');
    case 'liion': return `${fmt(el.num('capacity', 0) / 1000, 'Ah')} ${((el as Battery).soc * 100).toFixed(0)}%`;
    case 'charger': return `${fmt(el.num('chargeCurrent', 0), 'A')} 충전`;
    case 'dcdc': return `${fmt(el.num('vout', 0), 'V')} 출력`;
    case 'cpload': return fmt((el as ConstantPowerLoad).watts, 'W');
    case 'clock': return fmt(el.num('frequency', 1), 'Hz');
    case 'voltmeter': return fmt((el as Voltmeter).reading, 'V');
    case 'ammeter': return fmt((el as Ammeter).reading, 'A');
    default: return '';
  }
}

/** 소자 하나를 월드 좌표계에 그린다 (핀 사이 선분 기준) */
export function drawElement(d: DrawCtx, el: Element): void {
  const g = geometry(el);
  const { ctx } = d;

  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = d.color;
  ctx.fillStyle = d.color;
  ctx.lineWidth = d.selected ? 3.5 : 2.4;

  if (el.type === 'wire') {
    ctx.strokeStyle = d.leadA ?? d.color;
    line(ctx, g.x1, g.y1, g.x2, g.y2);
    ctx.restore();
    return;
  }

  if (el.usesLayout) {
    drawMultiPin(d, el);
    ctx.restore();
    return;
  }

  if (el.type === 'port') {
    drawPort(d, el as Port);
    ctx.restore();
    return;
  }

  if (el.type === 'logicin' || el.type === 'logicout' || el.type === 'clock') {
    drawLogicTerminal(d, el as LogicElement);
    ctx.restore();
    return;
  }

  if (el.pinCount === 1) {
    ctx.strokeStyle = d.leadA ?? d.color;
    drawGround(d, g, el);
    ctx.restore();
    return;
  }

  const bodyLen = Math.min(g.len * 0.62, 36);
  const leadLen = (g.len - bodyLen) / 2;

  // 리드선 (양쪽 노드 전압에 따라 색을 달리 줄 수 있다)
  ctx.strokeStyle = d.leadA ?? d.color;
  line(ctx, g.x1, g.y1, g.x1 + g.ux * leadLen, g.y1 + g.uy * leadLen);
  ctx.strokeStyle = d.leadB ?? d.color;
  line(ctx, g.x2, g.y2, g.x2 - g.ux * leadLen, g.y2 - g.uy * leadLen);
  ctx.strokeStyle = d.color;

  ctx.translate(g.cx, g.cy);
  ctx.rotate(g.angle);
  d.angle = g.angle;

  const h = bodyLen / 2;
  switch (el.type) {
    case 'resistor': drawResistor(ctx, h); break;
    case 'lamp': drawLamp(d, el as Lamp, h); break;
    case 'battery': drawBattery(d, el as Battery, h); break;
    case 'liion': drawBattery(d, el as Battery, h, true); break;
    case 'usb': drawUsb(d, h); break;
    case 'cpload': drawCpLoad(d, el as ConstantPowerLoad, h); break;
    case 'button': drawButton(d, el as PushButton, h); break;
    case 'vsource': drawSourceCircle(d, el as VoltageSource, h); break;
    case 'csource': drawCurrentSource(d, h); break;
    case 'switch': drawSwitch(d, el as Switch, h); break;
    case 'fuse': drawFuse(d, el as Fuse, h); break;
    case 'capacitor': drawCapacitor(d, el as Capacitor, h); break;
    case 'inductor': drawInductor(ctx, h); break;
    case 'diode': drawDiode(d, h, false); break;
    case 'led': drawDiode(d, h, true, el as Led); break;
    case 'voltmeter': drawMeter(d, h, 'V'); break;
    case 'ammeter': drawMeter(d, h, 'A'); break;
    default: drawResistor(ctx, h);
  }
  ctx.restore();
}

/* ───────── 개별 심볼 ───────── */

function line(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number): void {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}

function drawGround(d: DrawCtx, g: Geom, _el: Element): void {
  const { ctx } = d;
  const x = g.x1;
  const y = g.y1;
  line(ctx, x, y, x, y + 8);
  for (let i = 0; i < 3; i++) {
    const w = 11 - i * 3.5;
    line(ctx, x - w, y + 8 + i * 4, x + w, y + 8 + i * 4);
  }
}

function drawResistor(ctx: CanvasRenderingContext2D, h: number): void {
  const seg = (h * 2) / 7;
  ctx.beginPath();
  ctx.moveTo(-h, 0);
  for (let i = 0; i < 7; i++) {
    const x = -h + seg * (i + 0.5);
    ctx.lineTo(x, i % 2 === 0 ? -7 : 7);
  }
  ctx.lineTo(h, 0);
  ctx.stroke();
}

function drawLamp(d: DrawCtx, el: Lamp, h: number): void {
  const { ctx } = d;
  const r = Math.min(h, 13);
  const b = d.running ? el.brightness : 0;
  if (b > 0.02) {
    const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, r * 3.2);
    glow.addColorStop(0, `rgba(253, 224, 71, ${0.75 * b})`);
    glow.addColorStop(1, 'rgba(253, 224, 71, 0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(0, 0, r * 3.2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = b > 0.02 ? `rgba(254, 240, 138, ${0.25 + 0.75 * b})` : 'rgba(148,163,184,0.12)';
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = d.color;
  ctx.stroke();
  const k = r * 0.62;
  line(ctx, -k, -k, k, k);
  line(ctx, -k, k, k, -k);
  line(ctx, -h, 0, -r, 0);
  line(ctx, r, 0, h, 0);
}

function drawBattery(d: DrawCtx, el: Battery, h: number, lithium = false): void {
  const { ctx } = d;
  if (lithium) {
    // 셀 외곽 (원통형 셀 느낌)
    ctx.save();
    ctx.lineWidth = 1.3;
    ctx.strokeStyle = 'rgba(96,165,250,0.6)';
    ctx.fillStyle = 'rgba(30,58,110,0.35)';
    roundRect(ctx, -13, -16, 26, 32, 4);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }
  // pin0 = 음극(짧은 판), pin1 = 양극(긴 판)
  const gap = 5;
  line(ctx, -h, 0, -gap, 0);
  line(ctx, gap, 0, h, 0);
  ctx.lineWidth = 3;
  line(ctx, -gap, -6, -gap, 6);   // 음극
  line(ctx, gap, -12, gap, 12);   // 양극
  ctx.lineWidth = 2.4;

  if (el.num('capacity', 0) > 0) {
    // 잔량 게이지 (항상 수평)
    const w = 22;
    ctx.save();
    ctx.rotate(-(d.angle ?? 0));
    ctx.strokeStyle = 'rgba(148,163,184,0.55)';
    ctx.lineWidth = 1.2;
    ctx.strokeRect(-w / 2, 17, w, 5);
    ctx.fillStyle = el.soc > 0.2 ? '#4ade80' : '#f87171';
    ctx.fillRect(-w / 2 + 1, 18, (w - 2) * el.soc, 3);
    ctx.restore();
  }
}

function drawSourceCircle(d: DrawCtx, el: VoltageSource, h: number): void {
  const { ctx } = d;
  const r = Math.min(h, 13);
  line(ctx, -h, 0, -r, 0);
  line(ctx, r, 0, h, 0);
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.lineWidth = 1.8;
  if (el.str('waveform', 'dc') === 'dc') {
    upright(d, -r * 0.42, 0, '\u2212', 'bold 13px ui-sans-serif, system-ui, sans-serif', d.color);
    upright(d, r * 0.42, 0, '+', 'bold 12px ui-sans-serif, system-ui, sans-serif', d.color);
  } else {
    ctx.beginPath();
    for (let i = -r * 0.6; i <= r * 0.6; i += 0.6) {
      const y = -Math.sin((i / (r * 0.6)) * Math.PI) * r * 0.45;
      if (i === -r * 0.6) ctx.moveTo(i, y);
      else ctx.lineTo(i, y);
    }
    ctx.stroke();
  }
  ctx.lineWidth = 2.4;
}

function drawCurrentSource(d: DrawCtx, h: number): void {
  const { ctx } = d;
  const r = Math.min(h, 13);
  line(ctx, -h, 0, -r, 0);
  line(ctx, r, 0, h, 0);
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.stroke();
  line(ctx, -r * 0.5, 0, r * 0.4, 0);
  ctx.beginPath();
  ctx.moveTo(r * 0.6, 0);
  ctx.lineTo(r * 0.2, -4);
  ctx.lineTo(r * 0.2, 4);
  ctx.closePath();
  ctx.fill();
}

function drawSwitch(d: DrawCtx, el: Switch, h: number): void {
  const { ctx } = d;
  const closed = el.bool('closed', false);
  line(ctx, -h, 0, -h + 4, 0);
  line(ctx, h - 4, 0, h, 0);
  ctx.fillStyle = closed ? '#4ade80' : '#64748b';
  dot(ctx, -h + 4, 0, 2.8);
  dot(ctx, h - 4, 0, 2.8);
  ctx.strokeStyle = closed ? '#4ade80' : d.color;
  if (closed) line(ctx, -h + 4, 0, h - 4, 0);
  else line(ctx, -h + 4, 0, h - 6, -11);
  ctx.strokeStyle = d.color;
}

function drawFuse(d: DrawCtx, el: Fuse, h: number): void {
  const { ctx } = d;
  ctx.strokeRect(-h, -7, h * 2, 14);
  if (el.blown) {
    ctx.strokeStyle = '#f87171';
    line(ctx, -h * 0.5, -5, 0, 0);
    line(ctx, 0, 0, h * 0.5, 5);
    ctx.strokeStyle = d.color;
  } else {
    ctx.beginPath();
    ctx.moveTo(-h, 0);
    ctx.bezierCurveTo(-h / 2, -8, h / 2, 8, h, 0);
    ctx.stroke();
  }
}

function drawCapacitor(d: DrawCtx, _el: Capacitor, h: number): void {
  const { ctx } = d;
  const gap = 4;
  line(ctx, -h, 0, -gap, 0);
  line(ctx, gap, 0, h, 0);
  ctx.lineWidth = 3;
  line(ctx, -gap, -11, -gap, 11);
  line(ctx, gap, -11, gap, 11);
  ctx.lineWidth = 2.4;
}

function drawInductor(ctx: CanvasRenderingContext2D, h: number): void {
  const coils = 4;
  const r = (h * 2) / (coils * 2);
  ctx.beginPath();
  for (let i = 0; i < coils; i++) {
    ctx.arc(-h + r + i * r * 2, 0, r, Math.PI, 0, false);
  }
  ctx.stroke();
}

function drawDiode(d: DrawCtx, h: number, isLed: boolean, led?: Led): void {
  const { ctx } = d;
  const s = Math.min(h, 10);
  line(ctx, -h, 0, -s, 0);
  line(ctx, s, 0, h, 0);
  if (isLed && led && d.running && led.brightness > 0.02) {
    const b = led.brightness;
    const color = led.str('color', '#ff4d4d');
    const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, 30);
    glow.addColorStop(0, hexToRgba(color, 0.8 * b));
    glow.addColorStop(1, hexToRgba(color, 0));
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(0, 0, 30, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.beginPath();
  ctx.moveTo(-s, -9);
  ctx.lineTo(s, 0);
  ctx.lineTo(-s, 9);
  ctx.closePath();
  ctx.fillStyle = isLed && led ? led.str('color', '#ff4d4d') : d.color;
  ctx.fill();
  ctx.strokeStyle = d.color;
  ctx.lineWidth = 3;
  line(ctx, s, -9, s, 9);
  ctx.lineWidth = 2.4;
  if (isLed) {
    for (let i = 0; i < 2; i++) {
      const ox = -3 + i * 7;
      ctx.save();
      ctx.lineWidth = 1.4;
      line(ctx, ox, -12, ox + 5, -18);
      ctx.beginPath();
      ctx.moveTo(ox + 6, -19);
      ctx.lineTo(ox + 2.5, -16);
      ctx.lineTo(ox + 5, -13.5);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
  }
}

function drawMeter(d: DrawCtx, h: number, letter: string): void {
  const { ctx } = d;
  const r = Math.min(h, 14);
  line(ctx, -h, 0, -r, 0);
  line(ctx, r, 0, h, 0);
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(15,20,32,0.9)';
  ctx.fill();
  ctx.stroke();
  upright(d, 0, 0, letter, 'bold 13px ui-sans-serif, system-ui, sans-serif', d.color);
}

/** 소자 회전과 무관하게 항상 똑바로 보이는 글자 */
function upright(
  d: DrawCtx, x: number, y: number, text: string, font: string, color: string,
): void {
  const { ctx } = d;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(1, d.flip ?? 1);
  ctx.rotate(-(d.angle ?? 0));
  ctx.fillStyle = color;
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 0, 0);
  ctx.restore();
}

function dot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

export function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return `rgba(248,113,113,${alpha})`;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${alpha})`;
}


/* ═══════════════════ 3핀 이상 소자 ═══════════════════ */

function drawUsb(d: DrawCtx, h: number): void {
  const { ctx } = d;
  const w = Math.min(h, 16);
  line(ctx, -h, 0, -w, 0);
  line(ctx, w, 0, h, 0);
  ctx.save();
  ctx.fillStyle = 'rgba(15,20,32,0.95)';
  ctx.fillRect(-w, -10, w * 2, 20);
  ctx.strokeRect(-w, -10, w * 2, 20);
  ctx.restore();
  upright(d, 0, 0, 'USB', 'bold 8.5px ui-sans-serif, system-ui, sans-serif', d.color);
  upright(d, w + 5, -9, '+', 'bold 9px ui-sans-serif, system-ui, sans-serif', d.color);
}

/** 핀 i 리드선 */
function lead(d: DrawCtx, i: number, x1: number, y1: number, x2: number, y2: number): void {
  const { ctx } = d;
  ctx.save();
  ctx.strokeStyle = d.pinColors?.[i] ?? d.color;
  line(ctx, x1, y1, x2, y2);
  ctx.restore();
}

function arrowHead(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, size = 6): void {
  const a = Math.atan2(y2 - y1, x2 - x1);
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - size * Math.cos(a - 0.45), y2 - size * Math.sin(a - 0.45));
  ctx.lineTo(x2 - size * Math.cos(a + 0.45), y2 - size * Math.sin(a + 0.45));
  ctx.closePath();
  ctx.fill();
}

/** 로컬 좌표계: 원점 = 첫 핀(x1,y1), +x = 축 방향, +y = 수직(s 양수) 방향 */
function drawMultiPin(d: DrawCtx, el: Element): void {
  const { ctx } = d;
  const a = el.axis();
  ctx.translate(el.x1 * GRID, el.y1 * GRID);
  const angle = Math.atan2(a.uy, a.ux);
  ctx.rotate(angle);
  ctx.scale(1, a.flip);
  d.angle = angle;
  d.flip = a.flip;
  const L = a.len * GRID;

  switch (el.type) {
    case 'npn':
    case 'pnp': drawBjt(d, el as Bjt, L); break;
    case 'nmos':
    case 'pmos': drawMos(d, el as Mosfet, L); break;
    case 'opamp': drawOpAmp(d, L); break;
    case 'charger': {
      const c = el as ChargerModule;
      drawModule(d, el, L, '충전', 'rgba(30,64,120,0.55)',
        c.mode === '충전 완료' ? '#60a5fa' : c.charging ? '#f87171' : null);
      break;
    }
    case 'dcdc':
      drawModule(d, el, L, 'DC-DC', 'rgba(22,90,60,0.5)', (el as DcDcModule).running ? '#4ade80' : null);
      break;
    case 'bms': {
      const b = el as BmsModule;
      drawModule(d, el, L, '보호회로', 'rgba(90,60,20,0.55)', b.tripped ? '#f87171' : '#4ade80');
      break;
    }
    case 'subckt': drawSubckt(d, el as SubcktInstance, L); break;
    case 'and': case 'or': case 'not': case 'nand': case 'nor': case 'xor':
      drawGate(d, el as LogicGate, L);
      break;
    case 'dff': drawDff(d, el as DFlipFlop, L); break;
    case 'mcu': drawMcu(d, el as Microcontroller, L); break;
  }
}

function drawBjt(d: DrawCtx, el: Bjt, L: number): void {
  const { ctx } = d;
  const bx = L - 12;
  const G = GRID;
  // 외곽 원
  ctx.save();
  ctx.lineWidth = 1.4;
  ctx.strokeStyle = 'rgba(148,163,184,0.45)';
  ctx.beginPath();
  ctx.arc(L - 5, 0, 15, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
  lead(d, 0, 0, 0, bx, 0);
  ctx.lineWidth = 3;
  line(ctx, bx, -10, bx, 10);
  ctx.lineWidth = 2.4;
  lead(d, 1, bx, -5, L, -12);
  lead(d, 1, L, -12, L, -G);
  lead(d, 2, bx, 5, L, 12);
  lead(d, 2, L, 12, L, G);
  ctx.fillStyle = d.color;
  if (el.type === 'npn') arrowHead(ctx, bx, 5, L - 1, 11.5, 7);
  else arrowHead(ctx, L - 1, 11.5, bx + 2, 6, 7);
}

function drawMos(d: DrawCtx, el: Mosfet, L: number): void {
  const { ctx } = d;
  const G = GRID;
  const gx = L - 17;
  const cx = L - 11;
  lead(d, 0, 0, 0, gx, 0);
  ctx.lineWidth = 2.6;
  line(ctx, gx, -11, gx, 11);
  // 채널 (증가형: 3토막)
  for (const [y1, y2] of [[-12, -5], [-3.5, 3.5], [5, 12]]) line(ctx, cx, y1, cx, y2);
  ctx.lineWidth = 2.4;
  lead(d, 1, cx, -8.5, L, -8.5);
  lead(d, 1, L, -8.5, L, -G);
  lead(d, 2, cx, 8.5, L, 8.5);
  lead(d, 2, L, 8.5, L, G);
  // 바디-소스 연결 + 화살표
  line(ctx, cx, 0, L, 0);
  line(ctx, L, 0, L, 8.5);
  ctx.fillStyle = d.color;
  if (el.type === 'nmos') arrowHead(ctx, L - 2, 0, cx + 1, 0, 6);
  else arrowHead(ctx, cx + 3, 0, L - 3, 0, 6);
}

function drawOpAmp(d: DrawCtx, L: number): void {
  const { ctx } = d;
  const G = GRID;
  const x0 = 14;
  const x1 = L - 14;
  lead(d, 0, 0, -G, x0, -G);
  lead(d, 1, 0, G, x0, G);
  lead(d, 2, x1, 0, L, 0);
  ctx.beginPath();
  ctx.moveTo(x0, -30);
  ctx.lineTo(x1, 0);
  ctx.lineTo(x0, 30);
  ctx.closePath();
  ctx.save();
  ctx.fillStyle = 'rgba(15,20,32,0.9)';
  ctx.fill();
  ctx.restore();
  ctx.stroke();
  upright(d, x0 + 8, -G, '−', 'bold 13px ui-sans-serif, system-ui, sans-serif', d.color);
  upright(d, x0 + 8, G, '+', 'bold 12px ui-sans-serif, system-ui, sans-serif', d.color);
}

function drawModule(d: DrawCtx, el: Element, L: number, title: string, fill: string, led: string | null): void {
  const { ctx } = d;
  const G = GRID;
  const x0 = 12;
  const x1 = L - 12;
  lead(d, 0, 0, -G, x0, -G);
  lead(d, 1, 0, G, x0, G);
  lead(d, 2, x1, -G, L, -G);
  lead(d, 3, x1, G, L, G);

  // PCB 기판
  ctx.save();
  ctx.fillStyle = fill;
  roundRect(ctx, x0, -31, x1 - x0, 62, 5);
  ctx.fill();
  ctx.stroke();
  ctx.restore();

  const small = '7.5px ui-monospace, monospace';
  const labels = el.pinLayout().map((p) => p.label);
  upright(d, x0 + 10, -G, labels[0], small, '#cbd5e1');
  upright(d, x0 + 10, G, labels[1], small, '#cbd5e1');
  upright(d, x1 - 12, -G, labels[2], small, '#cbd5e1');
  upright(d, x1 - 12, G, labels[3], small, '#cbd5e1');
  upright(d, (x0 + x1) / 2, 0, title, 'bold 10px ui-sans-serif, system-ui, sans-serif', '#f1f5f9');
  if (d.running) statusLed(d, (x0 + x1) / 2, 22, led);
}

/** 모듈 위의 작은 상태 LED (null = 꺼짐) */
function statusLed(d: DrawCtx, lx: number, ly: number, led: string | null): void {
  const { ctx } = d;
  ctx.save();
  if (led) {
    const glow = ctx.createRadialGradient(lx, ly, 0, lx, ly, 9);
    glow.addColorStop(0, hexToRgba(led, 0.8));
    glow.addColorStop(1, hexToRgba(led, 0));
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(lx, ly, 9, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = led ?? '#475569';
  ctx.beginPath();
  ctx.arc(lx, ly, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** 모듈 인스턴스 상자의 절반 높이 (월드 px) */
export function subcktHalfHeight(rows: number): number {
  return Math.max(24, (rows - 1) * GRID + 16);
}

function drawSubckt(d: DrawCtx, el: SubcktInstance, L: number): void {
  const { ctx } = d;
  const G = GRID;
  const x0 = 10;
  const x1 = L - 10;
  const half = subcktHalfHeight(el.rows);
  const layout = el.pinLayout();
  layout.forEach((slot, i) => {
    const y = slot.s * G;
    if (slot.t === 0) lead(d, i, 0, y, x0, y);
    else lead(d, i, x1, y, L, y);
  });

  ctx.save();
  const color = el.str('color', '#7c5cff');
  ctx.fillStyle = hexToRgba(color, 0.28);
  roundRect(ctx, x0, -half, x1 - x0, half * 2, 6);
  ctx.fill();
  ctx.strokeStyle = d.selected ? d.color : hexToRgba(color, 0.95);
  ctx.stroke();
  ctx.restore();

  const small = '7.5px ui-monospace, monospace';
  layout.forEach((slot) => {
    const y = slot.s * G;
    if (slot.t === 0) upright(d, x0 + 4 + slot.label.length * 2.3, y, slot.label, small, '#ddd6fe');
    else upright(d, x1 - 4 - slot.label.length * 2.3, y, slot.label, small, '#ddd6fe');
  });
  // 이름 (길면 줄여서)
  const name = el.label.length > 9 ? el.label.slice(0, 8) + '…' : el.label;
  upright(d, (x0 + x1) / 2, el.rows === 1 ? -half + 9 : 0, name, 'bold 10px ui-sans-serif, system-ui, sans-serif', '#f5f3ff');
  if (!el.inner.length) upright(d, (x0 + x1) / 2, half - 8, '정의 없음', small, '#fca5a5');
}

/** 포트(단자) 표시: 점 + 이름표 */
export function drawPort(d: DrawCtx, el: Port): void {
  const { ctx } = d;
  const x = el.x1 * GRID;
  const y = el.y1 * GRID;
  const name = el.portName || '?';
  ctx.save();
  ctx.font = 'bold 9px ui-monospace, monospace';
  const w = Math.max(18, ctx.measureText(name).width + 12);
  const h = 14;
  const dir = el.side === 'left' ? -1 : 1;
  const bx = dir < 0 ? x - 6 - w : x + 6;
  ctx.fillStyle = 'rgba(124,92,255,0.25)';
  ctx.strokeStyle = d.selected ? d.color : '#a78bfa';
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  // 핀 쪽으로 뾰족한 이름표
  if (dir < 0) {
    ctx.moveTo(bx, y - h / 2);
    ctx.lineTo(bx + w, y - h / 2);
    ctx.lineTo(x - 1, y);
    ctx.lineTo(bx + w, y + h / 2);
    ctx.lineTo(bx, y + h / 2);
  } else {
    ctx.moveTo(bx + w, y - h / 2);
    ctx.lineTo(bx, y - h / 2);
    ctx.lineTo(x + 1, y);
    ctx.lineTo(bx, y + h / 2);
    ctx.lineTo(bx + w, y + h / 2);
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#ede9fe';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(name, bx + w / 2 + (dir < 0 ? -3 : 3), y + 0.5);
  ctx.fillStyle = d.leadA ?? '#a78bfa';
  ctx.beginPath();
  ctx.arc(x, y, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/* ─────────── 로직 ─────────── */

const HIGH = '#4ade80';
const LOW = '#475569';

/** 논리 입력/표시/클럭: 핀(x1,y1) 에서 몸통(x2,y2) 쪽으로 */
function drawLogicTerminal(d: DrawCtx, el: LogicElement): void {
  const { ctx } = d;
  const px = el.x1 * GRID;
  const py = el.y1 * GRID;
  const dx = el.x2 - el.x1;
  const dy = el.y2 - el.y1;
  const len = Math.hypot(dx, dy) || 1;
  const bx = px + (dx / len) * GRID;
  const by = py + (dy / len) * GRID;
  ctx.strokeStyle = d.leadA ?? d.color;
  line(ctx, px, py, px + (dx / len) * (GRID - 11), py + (dy / len) * (GRID - 11));
  ctx.strokeStyle = d.color;
  ctx.save();
  ctx.lineWidth = 1.8;
  if (el.type === 'logicout') {
    const lit = (el as LogicOutput).lit && d.running;
    if (lit) {
      const g = ctx.createRadialGradient(bx, by, 0, bx, by, 20);
      g.addColorStop(0, 'rgba(74,222,128,0.7)');
      g.addColorStop(1, 'rgba(74,222,128,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(bx, by, 20, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = lit ? HIGH : '#1e293b';
    ctx.beginPath();
    ctx.arc(bx, by, 9, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = lit ? '#052e16' : '#94a3b8';
    ctx.font = 'bold 9px ui-monospace, monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(lit ? '1' : '0', bx, by + 0.5);
  } else {
    const on = el.type === 'logicin' ? (el as LogicInput).bool('on', false) : !!el.outLevels[0];
    ctx.fillStyle = on ? 'rgba(74,222,128,0.25)' : 'rgba(15,20,32,0.95)';
    roundRect(ctx, bx - 11, by - 9, 22, 18, 4);
    ctx.fill();
    ctx.strokeStyle = on ? HIGH : d.color;
    ctx.stroke();
    ctx.fillStyle = on ? HIGH : '#94a3b8';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (el.type === 'logicin') {
      ctx.font = 'bold 11px ui-monospace, monospace';
      ctx.fillText(on ? '1' : '0', bx, by + 0.5);
    } else {
      // 클럭: 사각파 그림
      ctx.strokeStyle = on ? HIGH : '#94a3b8';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(bx - 7, by + 4);
      ctx.lineTo(bx - 4, by + 4);
      ctx.lineTo(bx - 4, by - 4);
      ctx.lineTo(bx + 1, by - 4);
      ctx.lineTo(bx + 1, by + 4);
      ctx.lineTo(bx + 5, by + 4);
      ctx.lineTo(bx + 5, by - 4);
      ctx.lineTo(bx + 7, by - 4);
      ctx.stroke();
    }
  }
  ctx.restore();
}

function drawGate(d: DrawCtx, el: LogicGate, L: number): void {
  const { ctx } = d;
  const G = GRID;
  const k = el.kind;
  const inverted = k === 'not' || k === 'nand' || k === 'nor';
  const h = k === 'not' ? 13 : 20;
  const x0 = k === 'not' ? 14 : 16;
  const xe = L - (inverted ? 22 : 14);

  // 입력 리드
  if (k === 'not') lead(d, 0, 0, 0, x0, 0);
  else {
    const inset = k === 'or' || k === 'nor' || k === 'xor' ? 6 : 0;
    lead(d, 0, 0, -G / 2 - 0, x0 + inset, -G / 2);
    lead(d, 0, 0, -G, 0, -G / 2);
    lead(d, 1, 0, G / 2, x0 + inset, G / 2);
    lead(d, 1, 0, G, 0, G / 2);
  }
  const outPin = k === 'not' ? 1 : 2;
  lead(d, outPin, inverted ? xe + 8 : xe, 0, L, 0);

  ctx.save();
  ctx.beginPath();
  if (k === 'and' || k === 'nand') {
    const mid = (x0 + xe) / 2;
    ctx.moveTo(x0, -h);
    ctx.lineTo(mid, -h);
    ctx.arc(mid, 0, h, -Math.PI / 2, Math.PI / 2);
    ctx.lineTo(x0, h);
    ctx.closePath();
  } else if (k === 'not') {
    ctx.moveTo(x0, -h);
    ctx.lineTo(xe, 0);
    ctx.lineTo(x0, h);
    ctx.closePath();
  } else {
    // OR 계열
    ctx.moveTo(x0, -h);
    ctx.quadraticCurveTo(x0 + (xe - x0) * 0.6, -h, xe, 0);
    ctx.quadraticCurveTo(x0 + (xe - x0) * 0.6, h, x0, h);
    ctx.quadraticCurveTo(x0 + 10, 0, x0, -h);
    ctx.closePath();
  }
  const on = !!el.outLevels[0] && d.running;
  ctx.fillStyle = on ? 'rgba(74,222,128,0.14)' : 'rgba(15,20,32,0.9)';
  ctx.fill();
  ctx.stroke();
  if (k === 'xor') {
    ctx.beginPath();
    ctx.moveTo(x0 - 6, -h);
    ctx.quadraticCurveTo(x0 + 4, 0, x0 - 6, h);
    ctx.stroke();
  }
  if (inverted) {
    ctx.beginPath();
    ctx.arc(xe + 4, 0, 4, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
  const name = k.toUpperCase();
  if (k !== 'not') upright(d, (x0 + xe) / 2 - 2, 0, name, 'bold 7.5px ui-monospace, monospace', on ? HIGH : '#94a3b8');
}

function drawDff(d: DrawCtx, el: DFlipFlop, L: number): void {
  const { ctx } = d;
  const G = GRID;
  const x0 = 12;
  const x1 = L - 12;
  lead(d, 0, 0, -G, x0, -G);
  lead(d, 1, 0, G, x0, G);
  lead(d, 2, x1, -G, L, -G);
  lead(d, 3, x1, G, L, G);
  ctx.save();
  ctx.fillStyle = 'rgba(15,20,32,0.92)';
  roundRect(ctx, x0, -31, x1 - x0, 62, 4);
  ctx.fill();
  ctx.stroke();
  // 클럭 입력 삼각형
  ctx.beginPath();
  ctx.moveTo(x0, G - 5);
  ctx.lineTo(x0 + 7, G);
  ctx.lineTo(x0, G + 5);
  ctx.stroke();
  ctx.restore();
  const small = 'bold 8px ui-monospace, monospace';
  upright(d, x0 + 8, -G, 'D', small, '#cbd5e1');
  upright(d, x0 + 15, G, 'CLK', '6.5px ui-monospace, monospace', '#94a3b8');
  const q = !!el.outLevels[0] && d.running;
  upright(d, x1 - 8, -G, 'Q', small, q ? HIGH : '#cbd5e1');
  upright(d, x1 - 8, G, 'Q̄', small, !q && d.running ? HIGH : '#cbd5e1');
  upright(d, (x0 + x1) / 2, 0, 'D-FF', 'bold 9px ui-sans-serif, system-ui, sans-serif', '#f1f5f9');
}
void LOW;

function drawMcu(d: DrawCtx, el: Microcontroller, L: number): void {
  const { ctx } = d;
  const G = GRID;
  const x0 = 12;
  const x1 = L - 12;
  const half = 76;
  const layout = el.pinLayout();
  layout.forEach((slot, i) => {
    const y = slot.s * G;
    if (slot.t === 0) lead(d, i, 0, y, x0, y);
    else lead(d, i, x1, y, L, y);
  });
  ctx.save();
  ctx.fillStyle = 'rgba(17,24,39,0.96)';
  roundRect(ctx, x0, -half, x1 - x0, half * 2, 5);
  ctx.fill();
  ctx.strokeStyle = d.selected ? d.color : '#64748b';
  ctx.stroke();
  // 칩 다리 느낌의 눈금
  ctx.strokeStyle = 'rgba(148,163,184,0.25)';
  ctx.lineWidth = 1;
  roundRect(ctx, x0 + 8, -half + 8, x1 - x0 - 16, half * 2 - 16, 3);
  ctx.stroke();
  ctx.restore();

  const small = '7.5px ui-monospace, monospace';
  layout.forEach((slot, i) => {
    const y = slot.s * G;
    const gpio = i - 2;
    let color = '#cbd5e1';
    if (gpio >= 0 && d.running && el.powered && el.modes[gpio] === 'out') {
      color = el.duty[gpio] >= 0.5 ? HIGH : '#94a3b8';
    }
    if (slot.label === 'VCC') color = '#fca5a5';
    if (slot.t === 0) upright(d, x0 + 14, y, slot.label, small, color);
    else upright(d, x1 - 14, y, slot.label, small, color);
  });
  upright(d, (x0 + x1) / 2, -18, 'MCU', 'bold 12px ui-sans-serif, system-ui, sans-serif', '#f1f5f9');
  upright(d, (x0 + x1) / 2, -4, 'JS', '8px ui-monospace, monospace', '#94a3b8');
  if (d.running) statusLed(d, (x0 + x1) / 2, 14, el.error ? '#f87171' : el.powered ? '#4ade80' : null);
}

function drawButton(d: DrawCtx, el: PushButton, h: number): void {
  const { ctx } = d;
  line(ctx, -h, 0, -h + 5, 0);
  line(ctx, h - 5, 0, h, 0);
  ctx.fillStyle = el.pressed ? '#4ade80' : d.color;
  dot(ctx, -h + 5, 0, 2.6);
  dot(ctx, h - 5, 0, 2.6);
  const y = el.pressed ? -3 : -9;
  ctx.save();
  ctx.strokeStyle = el.pressed ? '#4ade80' : d.color;
  line(ctx, -h + 3, y, h - 3, y);
  line(ctx, 0, y, 0, y - 7);
  ctx.lineWidth = 3;
  line(ctx, -5, y - 8, 5, y - 8);
  ctx.restore();
}

/** 정전력 부하 (전자기기) */
function drawCpLoad(d: DrawCtx, el: ConstantPowerLoad, h: number): void {
  const { ctx } = d;
  const w = Math.min(h, 17);
  line(ctx, -h, 0, -w, 0);
  line(ctx, w, 0, h, 0);
  ctx.save();
  const on = d.running && el.running;
  ctx.fillStyle = on ? 'rgba(250,204,21,0.18)' : 'rgba(15,20,32,0.95)';
  roundRect(ctx, -w, -11, w * 2, 22, 4);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
  const preset = el.str('preset', 'custom');
  const short: Record<string, string> = {
    screen: '화면', 'screen-max': '화면', 'cpu-idle': 'CPU', 'cpu-max': 'CPU',
    radio: '통신', camera: '카메라', speaker: '스피커', custom: '부하',
  };
  upright(d, 0, 0, short[preset] ?? LOAD_PRESETS[preset]?.label ?? '부하',
    'bold 8.5px ui-sans-serif, system-ui, sans-serif', on ? '#fde68a' : d.color);
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** 다핀 소자의 로컬 상자: x 는 0..L(축 방향), y 는 ±half (월드 px) */
function localHalf(el: Element): number {
  switch (el.type) {
    case 'subckt': return subcktHalfHeight((el as SubcktInstance).rows);
    case 'charger':
    case 'dcdc':
    case 'bms':
    case 'dff': return 32;
    case 'mcu': return 76;
    case 'opamp': return 31;
    case 'not': return 16;
    default: return 24;
  }
}

/** 소자가 차지하는 영역 (월드 좌표) — 히트 테스트·라벨 위치용 */
export function elementBounds(el: Element): { x1: number; y1: number; x2: number; y2: number } {
  if (el.usesLayout) {
    const a = el.axis();
    const L = a.len * GRID;
    const half = localHalf(el);
    const ox = el.x1 * GRID;
    const oy = el.y1 * GRID;
    const pts = [[0, -half], [L, -half], [0, half], [L, half]].map(([t, s]) => [
      ox + a.ux * t + a.vx * s * a.flip,
      oy + a.uy * t + a.vy * s * a.flip,
    ]);
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    return { x1: Math.min(...xs), y1: Math.min(...ys), x2: Math.max(...xs), y2: Math.max(...ys) };
  }
  const pins = el.pins;
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
  for (const p of pins) {
    x1 = Math.min(x1, p.x); x2 = Math.max(x2, p.x);
    y1 = Math.min(y1, p.y); y2 = Math.max(y2, p.y);
  }
  return { x1: x1 * GRID, y1: y1 * GRID, x2: x2 * GRID, y2: y2 * GRID };
}
